import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, ArrowLeft, ArrowRight, Loader2, Upload, FileCheck2, Save, Star, Lock, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_ACCENT = '#E60000';
const LIKERT_5 = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];

// Build an authenticated image URL (download endpoint needs a token query param)
export function fileUrl(path, token) {
  if (!path) return null;
  return `${BACKEND_URL}${path}${token ? `?token=${token}` : ''}`;
}

// Skip-logic: is a question visible given current answers?
function evalCondition(c, answers) {
  if (!c || !c.question_id) return true;
  const ref = answers[c.question_id];
  const vals = Array.isArray(c.values) && c.values.length ? c.values : (c.value ? [c.value] : []);
  if (vals.length === 0) return true;
  const matches = Array.isArray(ref) ? ref.some(r => vals.includes(r)) : vals.includes(ref);
  return c.operator === 'not_equals' ? !matches : matches;
}

export function isVisible(q, answers) {
  // Supports multiple conditions combined with AND/OR; falls back to the legacy single `condition`.
  let conds = Array.isArray(q?.conditions) ? q.conditions.filter(c => c && c.question_id) : [];
  if (conds.length === 0 && q?.condition && q.condition.question_id) conds = [q.condition];
  if (conds.length === 0) return true;
  const results = conds.map(c => evalCondition(c, answers));
  return (q?.condition_logic === 'OR') ? results.some(Boolean) : results.every(Boolean);
}

export default function SurveyFillPage() {
  const { surveyId } = useParams();
  const { getAuthHeader, sessionToken, employee } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPreview = new URLSearchParams(location.search).get('preview') === '1';
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [specify, setSpecify] = useState({});
  const [denied, setDenied] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [lastSaved, setLastSaved] = useState(null);

  const accent = survey?.theme_color || DEFAULT_ACCENT;
  const bg = survey?.background_color || 'transparent';
  const draftKey = useMemo(() => `survey-draft:${surveyId}:${employee?.employee_id || 'anon'}`, [surveyId, employee]);

  useEffect(() => {
    (async () => {
      const r = await fetch(`${API}/surveys/${surveyId}`, { headers: getAuthHeader() });
      if (r.ok) setSurvey(await r.json());
      else if (r.status === 403 || r.status === 404) setDenied(true);
    })();
    // Track that this user opened the form (skip in preview/test mode)
    if (!isPreview) fetch(`${API}/surveys/${surveyId}/view`, { method: 'POST', headers: getAuthHeader() }).catch(() => {});
  }, [surveyId, getAuthHeader, isPreview]);

  useEffect(() => {
    if (!survey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) { const saved = JSON.parse(raw); if (saved && typeof saved === 'object') setAnswers(saved); }
      const rawS = localStorage.getItem(`${draftKey}:specify`);
      if (rawS) { const savedS = JSON.parse(rawS); if (savedS && typeof savedS === 'object') setSpecify(savedS); }
    } catch { /* ignore */ }
  }, [survey, draftKey]);

  const setA = (qid, v) => setAnswers(a => ({ ...a, [qid]: v }));
  const setSpec = (qid, option, text) => setSpecify(s => ({ ...s, [qid]: { ...(s[qid] || {}), [option]: text } }));

  useEffect(() => {
    if (!survey || Object.keys(answers).length === 0) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify(answers));
        localStorage.setItem(`${draftKey}:specify`, JSON.stringify(specify));
        setLastSaved(new Date());
      } catch { /* ignore */ }
    }, 800);
    return () => clearTimeout(t);
  }, [answers, specify, survey, draftKey]);

  const pages = useMemo(() => {
    const qs = survey?.questions || [];
    const result = [];
    let current = { title: null, items: [] };
    qs.forEach(q => {
      if (q.type === 'section') {
        if (current.items.length || current.title) result.push(current);
        current = { title: q.label, items: [] };
      } else { current.items.push(q); }
    });
    if (current.items.length || current.title) result.push(current);
    const cleaned = result.filter((p, i) => p.items.length > 0 || (i === 0));
    return cleaned.length ? cleaned : [{ title: null, items: [] }];
  }, [survey]);

  const submit = async () => {
    if (isPreview) {
      toast.success('Preview only — this test response was not saved');
      setSubmitted(true);
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/surveys/${surveyId}/respond`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ survey_id: surveyId, answers, specify }),
      });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Submit failed'); return; }
      try { localStorage.removeItem(draftKey); localStorage.removeItem(`${draftKey}:specify`); } catch { /* ignore */ }
      // Mark completed so the pending-survey popup won't show it again on this browser
      try {
        const ckey = `survey-completed:${employee?.employee_id || 'anon'}`;
        const set = new Set(JSON.parse(localStorage.getItem(ckey) || '[]'));
        set.add(surveyId);
        localStorage.setItem(ckey, JSON.stringify([...set]));
      } catch { /* ignore */ }
      setSubmitted(true);
    } finally { setSaving(false); }
  };

  if (denied) {
    return (
      <Layout>
        <div className="max-w-lg mx-auto text-center py-20 space-y-4" data-testid="survey-access-denied">
          <div className="mx-auto h-16 w-16 rounded-full bg-red-50 flex items-center justify-center">
            <Lock className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight">You don't have access to this survey</h2>
          <p className="text-muted-foreground text-sm">This survey is only available to its selected audience. If you believe this is a mistake, please contact the survey owner.</p>
          <Button className="mt-2" onClick={() => navigate('/dashboard')} data-testid="denied-back-btn">Back to Dashboard</Button>
        </div>
      </Layout>
    );
  }

  if (!survey) return <Layout><Card><CardContent className="py-10 text-center">Loading…</CardContent></Card></Layout>;

  if (submitted) {
    return (
      <Layout>
        <div style={{ backgroundColor: bg }} className="min-h-full rounded-xl">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto text-center py-16 space-y-3">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 12 }}>
              <CheckCircle2 className="h-20 w-20 mx-auto" style={{ color: accent }} />
            </motion.div>
            <h2 className="text-3xl font-bold tracking-tight">{isPreview ? 'Preview complete' : (survey.thank_you_message || 'Thank you for your feedback!')}</h2>
            <p className="text-muted-foreground">{isPreview ? 'This was a test — no response was saved.' : 'Your response has been recorded.'}</p>
            {isPreview
              ? <Button className="mt-4" variant="outline" onClick={() => navigate(`/surveys/${surveyId}`)} data-testid="preview-done-btn">Back to Survey</Button>
              : <Button className="mt-4 text-white" style={{ backgroundColor: accent }} onClick={() => navigate('/dashboard')} data-testid="fill-done-btn">Back to Dashboard</Button>}
          </motion.div>
        </div>
      </Layout>
    );
  }

  const activeQs = (survey.questions || []).filter(q => q.type !== 'section' && isVisible(q, answers));
  const isAnswered = (q) => {
    const v = answers[q.id];
    if (v === undefined || v === '' || v === null) return false;
    if (Array.isArray(v) && v.length === 0) return false;
    if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) return false;
    return true;
  };
  const answered = activeQs.filter(isAnswered).length;
  const progress = activeQs.length ? Math.round((answered / activeQs.length) * 100) : 0;

  const currentPage = pages[page] || { title: null, items: [] };
  const visibleItems = currentPage.items.filter(q => isVisible(q, answers));
  const isLastPage = page >= pages.length - 1;
  let baseIndex = 0;
  for (let p = 0; p < page; p++) baseIndex += pages[p].items.filter(q => isVisible(q, answers)).length;

  const validatePage = () => {
    for (const q of visibleItems) {
      if (q.required && !isAnswered(q)) { toast.error(`"${q.label}" is required`); return false; }
      const cfg = q.option_config || {};
      if (Object.keys(cfg).length) {
        const v = answers[q.id];
        const selected = Array.isArray(v) ? v : (v ? [v] : []);
        for (const opt of selected) {
          const oc = cfg[opt] || {};
          if (oc.specify && oc.required && !((specify[q.id]?.[opt] || '').trim())) {
            toast.error(`Please specify for "${opt}" in "${q.label}"`); return false;
          }
        }
      }
    }
    return true;
  };
  const goNext = () => { if (validatePage()) setPage(p => Math.min(p + 1, pages.length - 1)); };
  const goBack = () => setPage(p => Math.max(p - 1, 0));

  const logoSrc = survey.logo_url ? `${BACKEND_URL}/api/public/survey-logo/${surveyId}` : null;

  return (
    <Layout>
      <div style={{ backgroundColor: bg }} className="min-h-full rounded-xl p-1 sm:p-4">
        <div className="max-w-3xl mx-auto space-y-5 pb-10" data-testid="survey-fill-page">
          {isPreview && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 flex items-center gap-2 text-sm font-medium" data-testid="preview-banner">
              <Eye className="h-4 w-4" />
              Preview / Test mode — this is how respondents will see the survey. Responses submitted here are <b>not saved</b>.
            </div>
          )}
          {/* Header */}
          <div className="rounded-2xl overflow-hidden border bg-white shadow-sm">
            <div className="h-2 w-full" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}66)` }} />
            <div className="p-6 sm:p-8">
              {logoSrc && <img src={logoSrc} alt="logo" className="h-16 mb-4 object-contain mx-auto block" data-testid="fill-logo" />}
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{survey.title}</h1>
              {survey.description && <p className="text-sm text-muted-foreground mt-2">{survey.description}</p>}
              <div className="mt-4">
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <motion.div className="h-full rounded-full" style={{ backgroundColor: accent }}
                    animate={{ width: `${progress}%` }} transition={{ type: 'spring', stiffness: 120, damping: 20 }} />
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <p className="text-xs text-muted-foreground">{answered} of {activeQs.length} answered{pages.length > 1 ? ` · Section ${page + 1} of ${pages.length}` : ''}</p>
                  {lastSaved && <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="autosave-indicator"><Save className="h-3 w-3" />Draft saved</p>}
                </div>
              </div>
            </div>
          </div>

          {employee && page === 0 && (
            <div className="rounded-xl border bg-white/70 p-5">
              <p className="text-xs font-bold tracking-[0.1em] uppercase text-muted-foreground mb-3">Your Details (auto-filled)</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <ReadOnly label="Name" value={employee.full_name} />
                <ReadOnly label="Employee Code" value={employee.employee_code} />
                <ReadOnly label="Department" value={employee.department} />
                <ReadOnly label="Designation" value={employee.designation} />
                <ReadOnly label="Location" value={employee.location} />
                <ReadOnly label="Entity" value={employee.entity} />
              </div>
            </div>
          )}

          {currentPage.title && <h3 className="text-xl font-semibold tracking-tight border-b pb-2" data-testid="section-title">{currentPage.title}</h3>}

          <AnimatePresence mode="wait">
            <motion.div key={page} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }} className="space-y-4">
              {visibleItems.map((q, i) => (
                <div key={q.id} className="rounded-xl border bg-white p-5 sm:p-6 shadow-sm" data-testid={`fill-question-${q.id}`}>
                  <Label className="text-base font-medium">{baseIndex + i + 1}. {q.label} {q.required && <span style={{ color: accent }}>*</span>}</Label>
                  {q.description && <p className="text-xs text-muted-foreground mt-1">{q.description}</p>}
                  <div className="mt-3">
                    <QuestionField q={q} value={answers[q.id]} onChange={(v) => setA(q.id, v)} getAuthHeader={getAuthHeader} accent={accent} />
                    <SpecifyInputs q={q} value={answers[q.id]} specify={specify[q.id]} onChange={(opt, text) => setSpec(q.id, opt, text)} accent={accent} />
                  </div>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>

          <div className="flex justify-between gap-2 pt-2">
            <Button variant="outline" onClick={page === 0 ? () => navigate('/dashboard') : goBack} data-testid="fill-back-btn">
              {page === 0 ? 'Cancel' : (<><ArrowLeft className="h-4 w-4 mr-1" />Back</>)}
            </Button>
            {isLastPage ? (
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button onClick={() => { if (validatePage()) submit(); }} disabled={saving} className="text-white" style={{ backgroundColor: accent }} data-testid="submit-response-btn">{saving ? 'Submitting…' : 'Submit'}</Button>
              </motion.div>
            ) : (
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button onClick={goNext} className="text-white" style={{ backgroundColor: accent }} data-testid="fill-next-btn">Next<ArrowRight className="h-4 w-4 ml-1" /></Button>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function ReadOnly({ label, value }) {
  return <div><Label className="text-xs">{label}</Label><Input value={value || '—'} readOnly className="bg-white/60 mt-1" /></div>;
}

function OptionChip({ label, active, onClick, accent }) {
  return (
    <motion.button type="button" onClick={onClick} whileTap={{ scale: 0.985 }}
      className="flex items-center gap-3 w-full text-left rounded-xl border px-4 py-3 text-sm transition-colors"
      style={active ? { borderColor: accent, backgroundColor: `${accent}12`, color: accent } : {}}>
      <span className="flex h-5 w-5 items-center justify-center rounded-full border shrink-0" style={active ? { borderColor: accent } : {}}>
        {active && <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: accent }} />}
      </span>
      <span className="font-medium">{label}</span>
    </motion.button>
  );
}

function FileUploadField({ q, value, onChange, getAuthHeader, accent }) {
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await fetch(`${API}/upload`, { method: 'POST', headers: getAuthHeader(), body: fd });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Upload failed'); return; }
      onChange({ url: d.url, filename: d.filename });
      toast.success('File uploaded');
    } finally { setUploading(false); }
  };
  return (
    <div className="space-y-2">
      <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl px-6 py-8 cursor-pointer hover:bg-muted/40 text-sm transition-colors">
        {uploading ? <Loader2 className="h-6 w-6 animate-spin" style={{ color: accent }} /> : <Upload className="h-6 w-6" style={{ color: accent }} />}
        <span className="font-medium">{uploading ? 'Uploading…' : 'Click to upload a file'}</span>
        <span className="text-xs text-muted-foreground">Images, PDF, Excel, Docs</span>
        <input type="file" className="hidden" onChange={handle} disabled={uploading} data-testid={`file-input-${q.id}`} />
      </label>
      {value?.filename && <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400"><FileCheck2 className="h-4 w-4" /> {value.filename}</div>}
    </div>
  );
}

function MatrixField({ q, value, onChange, multi, accent }) {
  const rows = q.rows || [];
  const cols = q.options || LIKERT_5;
  const val = (value && typeof value === 'object' && !Array.isArray(value)) ? value : {};
  const setRow = (row, colVal) => onChange({ ...val, [row]: colVal });
  return (
    <div className="overflow-x-auto rounded-xl border">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-muted/40">
            <th className="text-left p-3" />
            {cols.map(c => <th key={c} className="p-3 text-xs font-semibold text-center">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row} className="border-t">
              <td className="p-3 text-xs font-medium">{row}</td>
              {cols.map(c => {
                const selected = multi ? (Array.isArray(val[row]) && val[row].includes(c)) : val[row] === c;
                return (
                  <td key={c} className="p-3 text-center">
                    <button type="button" data-testid={`matrix-${q.id}-${row}-${c}`}
                      onClick={() => {
                        if (multi) {
                          const arr = Array.isArray(val[row]) ? val[row] : [];
                          setRow(row, selected ? arr.filter(x => x !== c) : [...arr, c]);
                        } else { setRow(row, c); }
                      }}
                      className={`inline-flex h-6 w-6 items-center justify-center border ${multi ? 'rounded-md' : 'rounded-full'}`}
                      style={selected ? { borderColor: accent, backgroundColor: accent } : {}}>
                      {selected && <span className={`bg-white ${multi ? 'h-2.5 w-2.5 rounded-[3px]' : 'h-2.5 w-2.5 rounded-full'}`} />}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StarRating({ count, value, onChange, accent, testidPrefix }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-2 flex-wrap">
      {Array.from({ length: count }, (_, i) => i + 1).map(n => {
        const active = (hover || Number(value)) >= n;
        return (
          <motion.button key={n} type="button" whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}
            onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => onChange(n)}
            data-testid={`${testidPrefix}-${n}`} className="p-0.5">
            <Star className="h-11 w-11 transition-colors" style={{ color: active ? accent : '#d1d5db', fill: active ? accent : 'transparent' }} />
          </motion.button>
        );
      })}
    </div>
  );
}

// Renders a "specify" free-text box under each selected option that has it enabled.
// Reused by fill page + builder/detail previews.
export function SpecifyInputs({ q, value, specify, onChange, accent = DEFAULT_ACCENT }) {
  const cfg = q?.option_config || {};
  if (!Object.keys(cfg).length) return null;
  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const rows = selected.filter(o => cfg[o]?.specify);
  if (!rows.length) return null;
  const spec = specify || {};
  return (
    <div className="mt-3 space-y-3">
      {rows.map(o => {
        const oc = cfg[o] || {};
        return (
          <div key={o} className="rounded-lg border-l-2 pl-3 py-1" style={{ borderColor: accent }}>
            <Label className="text-sm font-medium">{oc.prompt || 'Please specify'} {oc.required && <span style={{ color: accent }}>*</span>}</Label>
            <Input className="mt-1" value={spec[o] || ''} onChange={e => onChange(o, e.target.value)}
              placeholder="Type your answer…" data-testid={`specify-input-${q.id}-${o}`} />
          </div>
        );
      })}
    </div>
  );
}

export function QuestionField({ q, value, onChange, getAuthHeader, accent = DEFAULT_ACCENT }) {
  switch (q.type) {
    case 'short_text': return <Input value={value || ''} onChange={e => onChange(e.target.value)} />;
    case 'long_text': return <Textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={4} />;
    case 'number': return <Input type="number" value={value || ''} onChange={e => onChange(e.target.value)} />;
    case 'email': return <Input type="email" value={value || ''} onChange={e => onChange(e.target.value)} />;
    case 'phone': return <Input type="tel" value={value || ''} onChange={e => onChange(e.target.value)} />;
    case 'date': return <Input type="date" value={value || ''} onChange={e => onChange(e.target.value)} />;
    case 'time': return <Input type="time" value={value || ''} onChange={e => onChange(e.target.value)} />;
    case 'yes_no':
      return <div className="grid grid-cols-2 gap-2 max-w-sm">{['Yes', 'No'].map(o => <OptionChip key={o} label={o} active={value === o} onClick={() => onChange(o)} accent={accent} />)}</div>;
    case 'single_choice':
    case 'likert':
      return (
        <div className="space-y-2">
          {(q.options || (q.type === 'likert' ? LIKERT_5 : [])).map(o => <OptionChip key={o} label={o} active={value === o} onClick={() => onChange(o)} accent={accent} />)}
        </div>
      );
    case 'multi_choice':
      return (
        <div className="space-y-2">
          {(q.options || []).map(o => {
            const arr = Array.isArray(value) ? value : [];
            const checked = arr.includes(o);
            return <OptionChip key={o} label={o} active={checked} accent={accent} onClick={() => onChange(checked ? arr.filter(x => x !== o) : [...arr, o])} />;
          })}
        </div>
      );
    case 'dropdown':
      return (
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>{(q.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      );
    case 'rating_5':
      return <StarRating count={5} value={value} onChange={onChange} accent={accent} testidPrefix={`star5-${q.id}`} />;
    case 'star_10':
      return <StarRating count={10} value={value} onChange={onChange} accent={accent} testidPrefix={`star10-${q.id}`} />;
    case 'slider': {
      const min = q.min ?? 0, max = q.max ?? 10;
      const cur = value === undefined || value === '' ? min : Number(value);
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-4">
            <input type="range" min={min} max={max} value={cur} onChange={e => onChange(Number(e.target.value))}
              className="w-full h-2 rounded-full appearance-none cursor-pointer" data-testid={`slider-${q.id}`}
              style={{ accentColor: accent }} />
            <span className="text-2xl font-bold tabular-nums w-14 text-center" style={{ color: accent }}>{value === undefined || value === '' ? '—' : value}</span>
          </div>
          <div className="flex justify-between text-xs text-muted-foreground"><span>{q.scale_min_label || min}</span><span>{q.scale_max_label || max}</span></div>
        </div>
      );
    }
    case 'nps':
      return (
        <div className="flex gap-1.5 flex-wrap">
          {Array.from({ length: 11 }, (_, i) => i).map(n => (
            <motion.button key={n} type="button" whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }} onClick={() => onChange(n)}
              className="h-10 w-10 rounded-lg border text-sm font-semibold transition-colors"
              style={Number(value) === n ? { backgroundColor: accent, color: '#fff', borderColor: accent } : {}}>{n}</motion.button>
          ))}
        </div>
      );
    case 'likert_matrix': return <MatrixField q={q} value={value} onChange={onChange} multi={false} accent={accent} />;
    case 'likert_matrix_multi': return <MatrixField q={q} value={value} onChange={onChange} multi={true} accent={accent} />;
    case 'file': return <FileUploadField q={q} value={value} onChange={onChange} getAuthHeader={getAuthHeader} accent={accent} />;
    default: return <Input value={value || ''} onChange={e => onChange(e.target.value)} />;
  }
}
